import { ExclamationCircleIcon } from '@heroicons/react/20/solid';
import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import ApiClient from '../../utils/ApiClient';

const TrackingInput = forwardRef(({ onDataReceived }, ref) => {
    const [inputValue, setInputValue] = useState('');
    const [inputError, setInputError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const debounceDelay = 500;
    const debounceTimeout = useRef(null);
    const inputRef = useRef(null);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
        setTrackingValue: (value) => {
            setInputValue(value);
            setInputError(false);
            // Trigger submission after setting value
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
            debounceTimeout.current = setTimeout(() => {
                executeSubmission(value);
            }, debounceDelay);
        },
        focus: () => {
            inputRef.current?.focus();
            inputRef.current?.select();
        },
        clear: () => {
            setInputValue('');
            setInputError(false);
        }
    }));

    const handleInputChange = (e) => {
        const value = e.target.value;
        setInputValue(value);
        setInputError(false);

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            executeSubmission(value);
        }, debounceDelay);
    };

    const executeSubmission = async (value) => {
        setIsLoading(true);
        setInputError(false);

        try {
            // ApiClient (axios) attaches the JWT and handles 401 refresh/redirect centrally.
            const response = await ApiClient.post('/api/getreturntrackingdata', { trackingId: value });
            const data = response.data;

            if (
                data === null ||
                data === 0 ||
                data === undefined ||
                (Array.isArray(data) && data.length === 0) ||
                (typeof data === 'object' && !Array.isArray(data) && Object.keys(data).length === 0)
            ) {
                throw new Error('Invalid response: Data is empty');
            }

            onDataReceived(data, value); // Pass data and tracking ID to the parent component
        } catch (error) {
            console.error('Error:', error);
            setInputError(true);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="relative flex flex-col w-[400px]">
            <label htmlFor="tracking-id" className="text-sm font-medium text-gray-900">
                Scan Tracking Number
            </label>
            <div className="relative mt-1 rounded-md shadow-sm">
                <input
                    ref={inputRef}
                    id="tracking-id"
                    type="text"
                    placeholder="Tracking Number"
                    value={inputValue}
                    onChange={handleInputChange}
                    readOnly={isLoading}
                    className={`block w-full h-[38px] rounded-md border-0 py-1.5 pr-10 ${inputError ? 'text-red-900 ring-red-300 focus:ring-red-500' : 'text-gray-900 ring-gray-300 focus:ring-indigo-500'} placeholder:text-gray-400 sm:text-sm sm:leading-6 ${isLoading ? 'bg-gray-100' : ''}`}
                    aria-invalid={inputError}
                    aria-describedby={inputError ? 'error-message' : undefined}
                />
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    {isLoading && (
                        <svg className="animate-spin h-5 w-5 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    )}
                    {inputError && !isLoading && (
                        <ExclamationCircleIcon aria-hidden="true" className="h-5 w-5 text-red-500" />
                    )}
                </div>
            </div>
            {inputError && (
                <p id="error-message" className="absolute -bottom-10 text-sm text-red-600">
                    Unable to find tracking number for "{inputValue}"
                </p>
            )}
        </div>
    );
});

export default TrackingInput;
